import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2 } from "lucide-react";
import { supabase } from "../../lib/supabase";
import type { EcritureComptable } from "../../lib/comptable";
import { assertEcritureModifiablePourEcritureId } from "../../lib/exercices";
import { useExerciceEditableForEcriture } from "../../hooks/useExerciceEditable";
import { fmtDate } from "./BlocEcriture";
import CompteAutocomplete from "./CompteAutocomplete";
import { formatMontantFCFA, parseMontant as parseMontantFcfa } from "@/lib/formatMontant";
import { InputMontant } from "../ui/InputMontant";
import { formatCompte8 } from "@/lib/comptable/formatCompte";
import { generateUUID, cn } from "@/lib/utils";
import { inputFieldClass, modalOverlayClass, modalPanelClass } from "@/lib/ui/formFieldClasses";
import { pieceUrlIsLikelyImage, pieceUrlIsLikelyPdf } from "@/lib/filePiecePreview";

const fmtFcfa = (n: number) => formatMontantFCFA(Math.abs(n));

type EditableRow = {
  key: string;
  compte_numero: string;
  compte_libelle: string;
  debitStr: string;
  creditStr: string;
  /** Conservé pour l’INSERT (non affiché ; inchangé si l’utilisateur n’édite que le libellé compte). */
  libelle_ligne: string;
};

function mapLignesToRows(lignes: EcritureComptable["lignes"]): EditableRow[] {
  return (lignes || []).map((l) => ({
    key: l.id || generateUUID(),
    compte_numero: l.compte_numero ?? "",
    compte_libelle: l.compte_libelle ?? "",
    debitStr: l.debit ? String(Math.round(Number(l.debit))) : "",
    creditStr: l.credit ? String(Math.round(Number(l.credit))) : "",
    libelle_ligne: l.libelle_ligne ?? "",
  }));
}

function parseMontantPositif(s: string): number {
  return Math.max(0, parseMontantFcfa(s));
}

function totals(rows: EditableRow[]): { debit: number; credit: number } {
  return rows.reduce(
    (acc, r) => ({
      debit: acc.debit + Math.round(parseMontantPositif(r.debitStr)),
      credit: acc.credit + Math.round(parseMontantPositif(r.creditStr)),
    }),
    { debit: 0, credit: 0 }
  );
}

export interface EcritureEditModalProps {
  ecriture: EcritureComptable;
  onClose: () => void;
  onSaved: (ecritureId: string) => void;
  onValidated: (ecritureId: string) => void;
}

export default function EcritureEditModal({ ecriture, onClose, onSaved, onValidated }: EcritureEditModalProps) {
  const navigate = useNavigate();
  const isReadOnly = ecriture.statut === "validated";
  const exerciceLock = useExerciceEditableForEcriture(ecriture.id);
  const exerciceBloqueSaisie = !exerciceLock.isEditable || exerciceLock.loading;
  const cannotEditLines = isReadOnly || exerciceBloqueSaisie;
  const initialRef = useRef<EditableRow[]>([]);
  const [rows, setRows] = useState<EditableRow[]>(() => mapLignesToRows(ecriture.lignes));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showPiece, setShowPiece] = useState(true);
  const [pieceUrl, setPieceUrl] = useState<string | null>(null);
  const [narrowPieceLayout, setNarrowPieceLayout] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 899px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 899px)");
    const onChange = () => setNarrowPieceLayout(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    setShowPiece(true);
  }, [ecriture.id]);

  useEffect(() => {
    if (ecriture.source_type !== "facture_achat" || !ecriture.source_id) {
      setPieceUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.from("factures_achat").select("file_url").eq("id", ecriture.source_id).maybeSingle();
      if (cancelled) return;
      if (error) {
        console.warn("[EcritureEditModal] file_url facture achat:", error.message);
        setPieceUrl(null);
        return;
      }
      const url = (data?.file_url as string | null | undefined)?.trim() || null;
      setPieceUrl(url);
      if (!url) setShowPiece(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [ecriture.source_type, ecriture.source_id]);

  const lignesKey =
    ecriture.lignes?.map((l) => `${l.id}:${l.debit}:${l.credit}:${l.compte_numero}:${l.compte_libelle}`).join("|") ?? "";
  useEffect(() => {
    const mapped = mapLignesToRows(ecriture.lignes);
    initialRef.current = mapped.map((r) => ({ ...r }));
    setRows(mapped);
    setSaveError(null);
  }, [ecriture.id, ecriture.statut, lignesKey]);

  const resetOriginal = useCallback(() => {
    setRows(initialRef.current.map((r) => ({ ...r })));
    setSaveError(null);
  }, []);

  const { debit: totalDebit, credit: totalCredit } = totals(rows);
  const ecart = Math.abs(totalDebit - totalCredit);
  const balanced = ecart === 0;
  const canPersist = rows.length > 0 && balanced;

  const persistLignes = async (): Promise<void> => {
    await assertEcritureModifiablePourEcritureId(ecriture.id);
    const { error: delErr } = await supabase.from("ecritures_comptables_lignes").delete().eq("ecriture_id", ecriture.id);
    if (delErr) throw delErr;

    const inserts = rows.map((r) => ({
      ecriture_id: ecriture.id,
      compte_numero: formatCompte8(r.compte_numero.trim()) || formatCompte8("0"),
      compte_libelle: r.compte_libelle.trim() || "—",
      debit: Math.round(parseMontantPositif(r.debitStr)),
      credit: Math.round(parseMontantPositif(r.creditStr)),
      libelle_ligne: r.libelle_ligne.trim() || null,
    }));

    if (inserts.length > 0) {
      const { error: insErr } = await supabase.from("ecritures_comptables_lignes").insert(inserts);
      if (insErr) throw insErr;
    }
  };

  const handleSave = async () => {
    if (cannotEditLines) return;
    if (rows.length === 0) {
      setSaveError("Ajoutez au moins une ligne.");
      return;
    }
    if (!balanced) return;
    setSaving(true);
    setSaveError(null);
    try {
      await persistLignes();
      onSaved(ecriture.id);
      onClose();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    if (isReadOnly || rows.length === 0 || !balanced) return;
    setSaving(true);
    setSaveError(null);
    try {
      await persistLignes();
      const { error: upErr } = await supabase.from("ecritures_comptables").update({ statut: "validated" }).eq("id", ecriture.id);
      if (upErr) throw upErr;
      onValidated(ecriture.id);
      onClose();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Validation impossible.");
    } finally {
      setSaving(false);
    }
  };

  const updateRow = (key: string, patch: Partial<EditableRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const removeRow = (key: string) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        key: generateUUID(),
        compte_numero: "",
        compte_libelle: "",
        debitStr: "",
        creditStr: "",
        libelle_ligne: "",
      },
    ]);
  };

  const piecePanelVisible = Boolean(showPiece && pieceUrl);

  const ecritureBodyEl = (
    <>
      {!isReadOnly && exerciceBloqueSaisie && exerciceLock.message ? (
        <div
          style={{
            background: "#fef3c7",
            border: "1px solid #fcd34d",
            borderRadius: 10,
            padding: "12px 14px",
            fontSize: 13,
            color: "#92400e",
            marginBottom: 16,
          }}
          role="alert"
        >
          {exerciceLock.message}
        </div>
      ) : null}
      <div
        style={{
          background: "#fff7ed",
          border: "1px solid #fed7aa",
          borderRadius: 10,
          padding: "12px 14px",
          fontSize: 13,
          color: "#9a3412",
          marginBottom: 16,
        }}
      >
        Seules les écritures en brouillon sont modifiables. Une fois validée, l&apos;écriture est verrouillée.
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #e2e8f0", textAlign: "left" }}>
              <th style={{ padding: "10px 8px", color: "#64748b", fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Compte</th>
              <th style={{ padding: "10px 8px", color: "#64748b", fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Libellé</th>
              <th style={{ padding: "10px 8px", color: "#64748b", fontWeight: 700, fontSize: 11, textTransform: "uppercase", width: 120 }}>Débit</th>
              <th style={{ padding: "10px 8px", color: "#64748b", fontWeight: 700, fontSize: 11, textTransform: "uppercase", width: 120 }}>Crédit</th>
              <th style={{ width: 44 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "8px", verticalAlign: "middle" }}>
                  <CompteAutocomplete
                    value={r.compte_numero}
                    libelle={r.compte_libelle}
                    onChange={(c) => updateRow(r.key, { compte_numero: c.numero, compte_libelle: c.libelle })}
                    disabled={cannotEditLines}
                    allowCustom
                    placeholder="N° compte"
                    onRequestCreateAuxiliaire={(numero) =>
                      navigate("/parametres/plan-comptable", { state: { suggestAuxNumero: numero } })
                    }
                  />
                </td>
                <td style={{ padding: "8px", verticalAlign: "middle" }}>
                  <input
                    disabled={cannotEditLines}
                    value={r.compte_libelle}
                    onChange={(e) => updateRow(r.key, { compte_libelle: e.target.value })}
                    className={cn(inputFieldClass, cannotEditLines && "cursor-not-allowed bg-slate-50 text-slate-400")}
                    placeholder="Libellé du compte"
                  />
                </td>
                <td style={{ padding: "8px", verticalAlign: "middle" }}>
                  <InputMontant
                    disabled={cannotEditLines}
                    value={parseMontantPositif(r.debitStr)}
                    emptyWhenZero={r.debitStr.trim() === ""}
                    onChange={(n) => {
                      const v = Math.max(0, n);
                      updateRow(r.key, {
                        debitStr: v > 0 ? String(v) : "",
                        creditStr: v > 0 ? "" : r.creditStr,
                      });
                    }}
                    suffix=""
                    className={cn(cannotEditLines && "cursor-not-allowed bg-slate-50 text-slate-400")}
                  />
                </td>
                <td style={{ padding: "8px", verticalAlign: "middle" }}>
                  <InputMontant
                    disabled={cannotEditLines}
                    value={parseMontantPositif(r.creditStr)}
                    emptyWhenZero={r.creditStr.trim() === ""}
                    onChange={(n) => {
                      const v = Math.max(0, n);
                      updateRow(r.key, {
                        creditStr: v > 0 ? String(v) : "",
                        debitStr: v > 0 ? "" : r.debitStr,
                      });
                    }}
                    suffix=""
                    className={cn(cannotEditLines && "cursor-not-allowed bg-slate-50 text-slate-400")}
                  />
                </td>
                <td style={{ padding: "8px", verticalAlign: "middle", textAlign: "center" }}>
                  <button
                    type="button"
                    disabled={isReadOnly}
                    onClick={() => removeRow(r.key)}
                    title="Supprimer la ligne"
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      border: "none",
                      background: isReadOnly ? "#f1f5f9" : "#fef2f2",
                      color: "#dc2626",
                      cursor: isReadOnly ? "not-allowed" : "pointer",
                      fontSize: 18,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: "#f8fafc", fontWeight: 700 }}>
              <td colSpan={2} style={{ padding: "12px 8px", color: "#475569" }}>
                Total
              </td>
              <td style={{ padding: "12px 8px", textAlign: "right", color: "#0f172a" }}>{fmtFcfa(totalDebit)}</td>
              <td style={{ padding: "12px 8px", textAlign: "right", color: "#0f172a" }}>{fmtFcfa(totalCredit)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {!balanced && !cannotEditLines && (
        <p style={{ margin: "12px 0 0 0", fontSize: 13, color: "#dc2626", fontWeight: 600 }}>
          Débit et crédit doivent être égaux (écart : {fmtFcfa(ecart)})
        </p>
      )}

      {saveError && <p style={{ margin: "12px 0 0 0", fontSize: 13, color: "#dc2626" }}>{saveError}</p>}

      {!isReadOnly && (
        <button
          type="button"
          onClick={addRow}
          style={{
            marginTop: 14,
            background: "none",
            border: "none",
            padding: 0,
            color: "#FF9500",
            fontWeight: 600,
            fontSize: 13,
            cursor: "pointer",
            fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
          }}
        >
          + Ajouter une ligne
        </button>
      )}
    </>
  );

  const pieceJustificativeEl =
    pieceUrl &&
    piecePanelVisible && (
      <div
        style={{
          width: narrowPieceLayout ? "100%" : 420,
          maxWidth: narrowPieceLayout ? "100%" : 420,
          flexShrink: 0,
          maxHeight: narrowPieceLayout ? 300 : undefined,
          minHeight: 0,
          background: "#f8fafc",
          borderRight: narrowPieceLayout ? "none" : "1px solid #e4e8ef",
          borderBottom: narrowPieceLayout ? "1px solid #e4e8ef" : "none",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "12px 14px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            flexShrink: 0,
            background: "#f8fafc",
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>📄 Pièce justificative</span>
          <button
            type="button"
            aria-label="Masquer la pièce"
            onClick={() => setShowPiece(false)}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: "none",
              background: "#e2e8f0",
              color: "#64748b",
              cursor: "pointer",
              fontSize: 18,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0, padding: 12, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              background: "white",
              borderRadius: 8,
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {pieceUrlIsLikelyPdf(pieceUrl) ? (
              <iframe
                src={pieceUrl}
                title="Pièce justificative PDF"
                style={{
                  width: "100%",
                  height: "100%",
                  minHeight: narrowPieceLayout ? 120 : 0,
                  border: "none",
                  flex: 1,
                }}
              />
            ) : pieceUrlIsLikelyImage(pieceUrl) ? (
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 8,
                  background: "#f3f4f6",
                }}
              >
                <img
                  src={pieceUrl}
                  alt="Pièce justificative"
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 4 }}
                />
              </div>
            ) : (
              <div
                style={{
                  flex: 1,
                  minHeight: 200,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  padding: 16,
                  color: "#64748b",
                  fontSize: 13,
                  textAlign: "center",
                }}
              >
                <span>Aperçu non disponible pour ce type de fichier.</span>
                <button
                  type="button"
                  onClick={() => window.open(pieceUrl, "_blank", "noopener,noreferrer")}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "1px solid #fed7aa",
                    background: "#fff7ed",
                    color: "#c2410c",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                  }}
                >
                  Ouvrir la pièce ↗
                </button>
              </div>
            )}
          </div>
        </div>
        <div style={{ padding: "12px 14px", borderTop: "1px solid #e2e8f0", flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => window.open(pieceUrl, "_blank", "noopener,noreferrer")}
            style={{
              width: "100%",
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #fed7aa",
              background: "#fff7ed",
              color: "#c2410c",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
            }}
          >
            Ouvrir en grand ↗
          </button>
        </div>
      </div>
    );

  return (
    <div
      className={cn(modalOverlayClass, "z-[10050]")}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ecriture-edit-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={cn(
          modalPanelClass,
          "!max-w-none flex flex-col overflow-hidden shadow-none max-h-[min(90vh,880px)]",
          piecePanelVisible ? "max-w-[min(100vw,1300px)]" : "max-w-[min(100vw,920px)]"
        )}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex-shrink-0 border-b border-[#e4e8ef] px-5 py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2.5">
                  <h2 id="ecriture-edit-title" className="m-0 text-[14px] font-semibold text-slate-900">
                    Modifier l&apos;écriture
                  </h2>
                  {pieceUrl && !piecePanelVisible && (
                    <button
                      type="button"
                      onClick={() => setShowPiece(true)}
                      style={{
                        padding: "6px 12px",
                        borderRadius: 8,
                        border: "1px solid #fed7aa",
                        background: "#fff7ed",
                        color: "#c2410c",
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                        fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                      }}
                    >
                      📄 Voir la pièce
                    </button>
                  )}
                </div>
                <p className="mt-2 text-[12px] leading-snug text-slate-400">
                  <strong className="text-slate-600">{ecriture.journal_code}</strong>
                  {" · "}
                  {ecriture.libelle}
                  {" · "}
                  Pièce {fmtDate(ecriture.date_piece)}
                </p>
                <span
                  style={{
                    display: "inline-flex",
                    marginTop: 10,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    padding: "4px 10px",
                    borderRadius: 8,
                    background: isReadOnly ? "#ecfdf5" : "#fff7ed",
                    color: isReadOnly ? "#15803d" : "#c2410c",
                    border: isReadOnly ? "1px solid #bbf7d0" : "1px solid #fed7aa",
                  }}
                >
                  {isReadOnly ? "Validée" : "Brouillon"}
                </span>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Fermer"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  border: "none",
                  background: "#f1f5f9",
                  color: "#64748b",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <X size={22} />
              </button>
            </div>
          </div>

        {!piecePanelVisible ? (
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 24px" }}>{ecritureBodyEl}</div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: narrowPieceLayout ? "column" : "row",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              gap: 0,
            }}
          >
            {pieceJustificativeEl}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                overflowY: "auto",
                padding: "20px 24px",
              }}
            >
              {ecritureBodyEl}
            </div>
          </div>
        )}

        <div
            style={{
              padding: "16px 24px",
              borderTop: "1px solid #e2e8f0",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexShrink: 0,
            }}
          >
            {isReadOnly ? (
              <p style={{ margin: 0, fontSize: 13, color: "#64748b", width: "100%", textAlign: "center" }}>
                Écriture validée — lecture seule
              </p>
            ) : (
              <>
                <button
                  type="button"
                  disabled={saving || exerciceBloqueSaisie}
                  onClick={resetOriginal}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 10,
                    border: "1px solid #e2e8f0",
                    background: "white",
                    color: "#475569",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: saving || exerciceBloqueSaisie ? "not-allowed" : "pointer",
                    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                  }}
                >
                  Réinitialiser
                </button>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginLeft: "auto" }}>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={onClose}
                    style={{
                      padding: "10px 16px",
                      borderRadius: 10,
                      border: "1px solid #e2e8f0",
                      background: "white",
                      color: "#475569",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: saving ? "not-allowed" : "pointer",
                      fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    disabled={saving || !balanced || exerciceBloqueSaisie}
                    onClick={handleSave}
                    style={{
                      padding: "10px 16px",
                      borderRadius: 10,
                      border: "none",
                      background: saving || !balanced || exerciceBloqueSaisie ? "#e2e8f0" : "#FF9500",
                      color: saving || !balanced || exerciceBloqueSaisie ? "#94a3b8" : "white",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: saving || !balanced || exerciceBloqueSaisie ? "not-allowed" : "pointer",
                      fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {saving ? <Loader2 size={16} className="animate-spin" style={{ animation: "spin 1s linear infinite" }} /> : null}
                    Enregistrer
                  </button>
                  <button
                    type="button"
                    disabled={saving || !canPersist || exerciceBloqueSaisie}
                    onClick={handleValidate}
                    style={{
                      padding: "10px 16px",
                      borderRadius: 10,
                      border: "none",
                      background: saving || !canPersist || exerciceBloqueSaisie ? "#e2e8f0" : "#15803d",
                      color: saving || !canPersist || exerciceBloqueSaisie ? "#94a3b8" : "white",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: saving || !canPersist || exerciceBloqueSaisie ? "not-allowed" : "pointer",
                      fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                    }}
                  >
                    Valider définitivement
                  </button>
                </div>
              </>
            )}
          </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
